import React, { useState } from 'react';

import { useSelector } from 'react-redux';

import { useDevice } from '@bonnet/next/device';

import {
    Button,
    Image,
    Text,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { psxLearnMoreLinkClick, psxVideoStart } from 'reaxl-analytics-handlers';
import { Video, VideoPlaceholder } from 'reaxl-video-player';

import { placeholderImages } from 'axl-config';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import MediaGalleryModal from '@/containers/vdp/MediaGallery/MediaGalleryModal';

import LazyComponent from '@/components/LazyComponent';

const videoData = (uiContext) => ({
    psxHowItWorks: {
        key: 'mv-overlay-videos-psxHowItWorks',
        videoThumbnail: '/cm-api/content/static/img/psx/explainer/meant_to_be_hero_v4.3.frame1-q5.jpg',
        src: '/cm-api/content/static/video/psx/explainer/meant_to_be_hero_v4.3.mp4',
        modalTitle: 'How Private Seller Exchange Works',
        text: uiContext === 'link' ? 'See How Private Seller Exchange Works' : 'See How It Works',
        modalTriggerThumbnail: uiContext === 'thumbnail' && '/cm-api/content/static/img/psx/explainer/how_it_works_video_thumbnail.webp',
        additionalContent: (
            <ol className="text-size-500 text-bold padding-left-5 margin-top-2 margin-top-sm-5 margin-horizontal-sm-3 margin-horizontal-md-6">
                <li>
                    Shop Verified Private Vehicles
                    <Text
                        componentClass="p"
                        size={300}
                        weight="normal"
                    >
                        Once you&rsquo;ve found your dream car on our marketplace, send a chat, schedule a test drive, and negotiate pricing directly with the verified private seller - all online.
                    </Text>
                </li>
                <li>
                    Buy Online
                    <Text
                        componentClass="p"
                        size={300}
                        weight="normal"
                    >
                        Need financing? Private Seller Exchange partners with top lending institutions to offer buyers in many states the option to finance without stepping foot into a bank. You can also pay with your own bank or with cash. When you&rsquo;re ready to buy, make an offer.
                    </Text>
                </li>
                <li>
                    Receive a Clean Title
                    <Text
                        componentClass="p"
                        size={300}
                        weight="normal"
                    >
                        After you complete your purchase, you can drive away knowing you&rsquo;ll receive a clean title. In many cases, Private Seller Exchange will even step in as a deal facilitator to handle your tax, title, &amp; licensing so that you don&rsquo;t have to.
                    </Text>
                </li>
            </ol>
        ),
    },
    psxHowToPay: {
        key: 'mv-overlay-videos-psxHowToPay',
        videoThumbnail: '/cm-api/content/static/img/psx/explainer/psx_how_to_pay.png',
        src: '/cm-api/content/static/video/psx/explainer/psx_how_to_pay.mp4',
        modalTitle: 'How to Pay with Private Seller Exchange',
        text: 'See How to Pay',
        modalTriggerThumbnail: '/cm-api/content/static/img/psx/explainer/how_to_pay_video_thumbnail.png',
    },
});

// set up modal trigger
function PsxVideoModalTrigger({
    uiContext,
    modalTriggerThumbnail,
    handleModalOpen = () => { },
    children,
}) {

    switch (uiContext) {
        case 'thumbnail':
            return (
                <Button
                    className="display-flex align-items-center margin-top-1 text-bold gap-2"
                    onClick={handleModalOpen}
                    bsStyle="text"
                >
                    <Image
                        src={modalTriggerThumbnail}
                        aria-hidden="true"
                        height={40}
                        width={52}
                        className="rounded"
                        alt=""
                        lazyload
                    />
                    {children}
                </Button>
            );
        case 'link':
            return (
                <Button
                    block
                    bsStyle="link"
                    className="text-pre-wrap margin-top-2"
                    onClick={handleModalOpen}
                >
                    {children}
                </Button>
            );
        default:

    }
}

// set up MediaGalleryModal for PSX video
function PsxMediaGalleryModalContainer({
    videoId,
    uiContext,
}) {

    const device = useDevice();
    const isXs = device?.is?.xs;

    const { id: listingId } = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};

    const onPlay = (event) => {
        sendClick(psxVideoStart, event, { listingId });
    };

    const [showMediaGalleryModal, setShowMediaGalleryModal] = useState(false);

    const handleModalOpen = (event) => {
        if (videoId === 'psxHowItWorks' && uiContext === 'link') {
            sendClick(psxLearnMoreLinkClick, event, { listingId });
        }
        sendClick(psxVideoStart, event, { listingId });
        setShowMediaGalleryModal(true);
    };
    const handleModalClose = () => {
        setShowMediaGalleryModal(false);
    };

    const { key, videoThumbnail, src, modalTitle, text, modalTriggerThumbnail, additionalContent } = videoData(uiContext)[videoId] ?? {};

    const height = isXs ? 210 : 350;

    // set up Reaxl Video data structure for our PSX video file
    const videoOverlayMediaSet = [{
        tabTitle: 'Video',
        mediaDataSet: [{
            element: (
                <>
                    <LazyComponent fallback={(
                        <VideoPlaceholder
                            width="revert-layer"
                            height={height}
                            image={videoThumbnail || placeholderImages.noImageAvailable}
                        />
                    )}
                    >
                        <Video
                            className="display-flex margin-vertical-2 justify-content-center video-player"
                            key={key}
                            image={videoThumbnail}
                            src={src}
                            videoId={videoId}
                            width="revert-layer"
                            height={height}
                            onPlayerReady={() => { }} // need noops here to satisfy required callback
                            onPlay={onPlay}
                            placeholder={<VideoPlaceholder image={videoThumbnail || placeholderImages.noImageAvailable} />}
                            onReplay={() => { }}
                            onCompleted={() => { }}
                        />
                    </LazyComponent>
                    {additionalContent}
                </>
            ),
        }],
    }];

    return (
        <>
            <PsxVideoModalTrigger
                uiContext={uiContext}
                modalTriggerThumbnail={modalTriggerThumbnail}
                handleModalOpen={handleModalOpen}
            >
                {text}
            </PsxVideoModalTrigger>
            <MediaGalleryModal
                modalTitle={modalTitle}
                show={showMediaGalleryModal}
                hideMediaGaleryModal={handleModalClose}
                overlayMediaSet={videoOverlayMediaSet} // video content
                showFullScreen={isXs}
                isMobile={isXs}
                disableTabs={!!additionalContent}
            />
        </>
    );
}

export default PsxMediaGalleryModalContainer;
